import {
  ChevronDownIcon,
  Code2Icon,
  type LucideIcon,
  MessageSquareTextIcon,
  MinusIcon,
  NetworkIcon,
  TrendingDownIcon,
  TrendingUpIcon,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { Separator } from '@/components/ui/separator'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { COMPANIES, type CompanyId } from '../data/company-data'
import {
  getReadinessBreakdown,
  getReadinessHistory,
  type Pillar,
  READINESS_TARGETS,
  type ReadinessTarget,
  readinessBand,
  type Trend,
} from '../data/readiness-data'
import { useCompanies } from '../providers/company-provider'

const RING_SIZE = 128
const RING_STROKE = 10
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS

const PILLAR_IDENTITY: Record<
  Pillar,
  { icon: LucideIcon; text: string; chip: string }
> = {
  coding: {
    icon: Code2Icon,
    text: 'text-task-leetcode',
    chip: 'bg-task-leetcode/12 text-task-leetcode',
  },
  systemDesign: {
    icon: NetworkIcon,
    text: 'text-task-design',
    chip: 'bg-task-design/12 text-task-design',
  },
  behavioral: {
    icon: MessageSquareTextIcon,
    text: 'text-task-article',
    chip: 'bg-task-article/12 text-task-article',
  },
}

/** Brand logos served from thesvg.org */
function companyLogoUrl(id: CompanyId) {
  return `https://thesvg.org/icons/${id}/default.svg`
}

/** Animates a number toward its target with an ease-out curve. */
function useCountUp(target: number, duration = 900) {
  const [value, setValue] = useState(0)
  const fromRef = useRef(0)

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      fromRef.current = target
      setValue(target)
      return
    }
    const from = fromRef.current
    if (from === target) return
    let raf: number
    const start = performance.now()
    const tick = (now: number) => {
      const t = Math.min((now - start) / duration, 1)
      const eased = 1 - (1 - t) ** 3
      setValue(Math.round(from + (target - from) * eased))
      if (t < 1) raf = requestAnimationFrame(tick)
      else fromRef.current = target
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [target, duration])

  return value
}

function ScoreRing({ score }: { score: number }) {
  const animated = useCountUp(score)
  const band = readinessBand(animated)
  const filled = (animated / 100) * RING_CIRCUMFERENCE

  return (
    <div
      className="relative shrink-0"
      style={{ width: RING_SIZE, height: RING_SIZE }}
    >
      <svg
        width={RING_SIZE}
        height={RING_SIZE}
        viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
        className="-rotate-90"
        aria-hidden="true"
      >
        <circle
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={RING_RADIUS}
          fill="none"
          strokeWidth={RING_STROKE}
          className="stroke-muted"
        />
        <circle
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={RING_RADIUS}
          fill="none"
          strokeWidth={RING_STROKE}
          strokeLinecap="round"
          strokeDasharray={`${filled} ${RING_CIRCUMFERENCE - filled}`}
          className={cn(
            'transition-colors duration-300',
            band.band === 'red' && 'stroke-ready-red',
            band.band === 'orange' && 'stroke-ready-orange',
            band.band === 'yellow' && 'stroke-ready-yellow',
            band.band === 'green-light' && 'stroke-ready-green-light',
            band.band === 'green-dark' && 'stroke-ready-green-dark',
          )}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={cn('text-3xl font-bold tabular-nums', band.textClass)}>
          {animated}
        </span>
        <span className="text-[10px] font-medium tracking-wide text-muted-foreground uppercase">
          / 100
        </span>
      </div>
    </div>
  )
}

function HistoryBars({ target }: { target: ReadinessTarget }) {
  const history = getReadinessHistory(target)
  const max = 100

  return (
    <div
      className="flex h-24 items-end gap-1.5 sm:gap-2"
      role="img"
      aria-label="Readiness score by sprint"
    >
      {history.map((entry, i) => {
        const band = readinessBand(entry.score)
        const prev = history[i - 1]
        const delta = prev ? entry.score - prev.score : 0
        const isCurrent = i === history.length - 1

        return (
          <Tooltip key={entry.sprintNumber}>
            <TooltipTrigger asChild>
              <div className="flex h-full w-7 flex-col items-center justify-end gap-1 sm:w-9">
                <div
                  className={cn(
                    'w-full rounded-sm transition-all',
                    band.bgClass,
                    isCurrent
                      ? 'opacity-100 ring-2 ring-ring/40 ring-offset-1 ring-offset-background'
                      : 'opacity-55 hover:opacity-90',
                  )}
                  style={{ height: `${(entry.score / max) * 100}%` }}
                />
                <span
                  className={cn(
                    'text-[10px] leading-none tabular-nums',
                    isCurrent
                      ? 'font-semibold text-foreground'
                      : 'text-muted-foreground',
                  )}
                >
                  {entry.sprintNumber}
                </span>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <div className="flex flex-col gap-0.5">
                <span className="font-medium">
                  Sprint {entry.sprintNumber} · week of {entry.weekLabel}
                </span>
                <span>
                  Score {entry.score} — {band.label}
                  {prev &&
                    ` (${delta >= 0 ? '+' : ''}${delta} vs sprint ${prev.sprintNumber})`}
                </span>
              </div>
            </TooltipContent>
          </Tooltip>
        )
      })}
    </div>
  )
}

function TrendBadge({ trend, delta }: { trend: Trend; delta: number }) {
  return (
    <span
      className={cn(
        'flex items-center gap-0.5 text-xs font-medium tabular-nums',
        trend === 'up' && 'text-ready-green-dark',
        trend === 'down' && 'text-ready-red',
        trend === 'flat' && 'text-muted-foreground',
      )}
    >
      {trend === 'up' && (
        <TrendingUpIcon className="size-3.5" aria-hidden="true" />
      )}
      {trend === 'down' && (
        <TrendingDownIcon className="size-3.5" aria-hidden="true" />
      )}
      {trend === 'flat' && (
        <MinusIcon className="size-3.5" aria-hidden="true" />
      )}
      {trend === 'flat' ? 'steady' : `${delta > 0 ? '+' : ''}${delta}`}
    </span>
  )
}

function PillarBar({ score }: { score: number }) {
  const band = readinessBand(score)
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
      <div
        className={cn(
          'h-full rounded-full transition-all duration-500',
          band.bgClass,
        )}
        style={{ width: `${score}%` }}
      />
    </div>
  )
}

function ReadinessBreakdown({
  target,
  targetLabel,
}: {
  target: ReadinessTarget
  targetLabel: string
}) {
  const breakdown = getReadinessBreakdown(target)

  return (
    <div className="flex flex-col gap-5">
      <p className="text-xs text-muted-foreground">
        {target === 'general'
          ? 'Scores are measured against a general big-tech bar across all three pillars.'
          : `Scores are measured against ${targetLabel}'s bar — how strong each pillar needs to be to pass their loop, weighted by how much ${targetLabel} cares about it.`}
      </p>
      <div className="grid gap-6 md:grid-cols-3">
        {breakdown.map((pillar) => {
          const band = readinessBand(pillar.score)
          const identity = PILLAR_IDENTITY[pillar.pillar]
          const PillarIcon = identity.icon
          return (
            <div key={pillar.pillar} className="flex flex-col gap-3">
              <div className="flex items-baseline justify-between gap-2">
                <span className="flex items-center gap-1.5 text-sm font-semibold">
                  <span
                    className={cn(
                      'flex size-6 items-center justify-center rounded-md',
                      identity.chip,
                    )}
                  >
                    <PillarIcon className="size-3.5" aria-hidden="true" />
                  </span>
                  <span className={identity.text}>{pillar.label}</span>
                </span>
                <TrendBadge trend={pillar.trend} delta={pillar.delta} />
              </div>
              <div className="flex items-center gap-3">
                <span
                  className={cn(
                    'text-2xl font-bold tabular-nums',
                    band.textClass,
                  )}
                >
                  {pillar.score}%
                </span>
                <div className="flex-1">
                  <PillarBar score={pillar.score} />
                </div>
              </div>
              <span className="text-xs text-muted-foreground">
                {pillar.weight}% of{' '}
                {target === 'general' ? 'overall' : `your ${targetLabel}`} score
              </span>
              <ul className="flex flex-col gap-1.5">
                {pillar.skills.map((skill) => (
                  <li
                    key={skill.name}
                    className="flex items-center justify-between gap-2 text-sm"
                  >
                    <span className="truncate text-muted-foreground">
                      {skill.name}
                    </span>
                    <span className="flex shrink-0 items-center gap-2">
                      <span className="font-medium tabular-nums">
                        {skill.score}%
                      </span>
                      <TrendBadge trend={skill.trend} delta={skill.delta} />
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function CompanyToggle({
  companies,
  active,
  onSelect,
}: {
  companies: CompanyId[]
  active: CompanyId
  onSelect: (id: CompanyId) => void
}) {
  return (
    <div
      role="radiogroup"
      aria-label="Readiness target company"
      className="mt-1 flex items-center gap-1.5"
    >
      {companies.map((id) => {
        const isActive = id === active
        return (
          <Tooltip key={id}>
            <TooltipTrigger asChild>
              <button
                type="button"
                role="radio"
                aria-checked={isActive}
                onClick={() => onSelect(id)}
                aria-label={`Show ${COMPANIES[id].name} readiness`}
                className={cn(
                  'flex size-9 items-center justify-center rounded-lg border bg-white p-1.5 transition-all focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none',
                  isActive
                    ? 'border-primary ring-2 ring-primary/30'
                    : 'border-border opacity-50 grayscale hover:opacity-90 hover:grayscale-0',
                )}
              >
                <img
                  src={companyLogoUrl(id)}
                  alt=""
                  width={24}
                  height={24}
                  className="size-full object-contain"
                />
              </button>
            </TooltipTrigger>
            <TooltipContent>{COMPANIES[id].name} readiness</TooltipContent>
          </Tooltip>
        )
      })}
    </div>
  )
}

export function ReadinessScore() {
  const { selected } = useCompanies()
  const [activeCompany, setActiveCompany] = useState<CompanyId | null>(null)

  const effectiveCompany: CompanyId | null =
    selected.length === 0
      ? null
      : activeCompany && selected.includes(activeCompany)
        ? activeCompany
        : selected[0]

  const target: ReadinessTarget = effectiveCompany ?? 'general'
  const targetLabel =
    READINESS_TARGETS.find((t) => t.value === target)?.label ??
    'General interview prep'

  const history = getReadinessHistory(target)
  const current = history[history.length - 1]
  const previous = history[history.length - 2]
  const band = readinessBand(current.score)
  const delta = previous ? current.score - previous.score : 0

  const [open, setOpen] = useState(false)

  return (
    <Card className="py-0">
      <Collapsible open={open} onOpenChange={setOpen}>
        <CardContent className="flex flex-col gap-6 py-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-5">
            <ScoreRing score={current.score} />
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
                {effectiveCompany
                  ? `${targetLabel} readiness`
                  : 'Interview readiness'}
              </span>
              <span className={cn('text-xl font-semibold', band.textClass)}>
                {band.label}
              </span>
              <span className="flex items-center gap-1 text-sm text-muted-foreground">
                {delta !== 0 &&
                  (delta > 0 ? (
                    <TrendingUpIcon
                      className="size-4 text-ready-green-dark"
                      aria-hidden="true"
                    />
                  ) : (
                    <TrendingDownIcon
                      className="size-4 text-ready-red"
                      aria-hidden="true"
                    />
                  ))}
                {delta > 0 ? `+${delta}` : delta}{' '}
                {`this sprint · ${targetLabel}`}
              </span>
              {selected.length > 0 ? (
                <CompanyToggle
                  companies={selected}
                  active={effectiveCompany as CompanyId}
                  onSelect={setActiveCompany}
                />
              ) : (
                <span className="mt-1 text-xs text-muted-foreground">
                  No target companies set — add them in Settings for
                  company-specific scores.
                </span>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
              Score history by sprint
            </span>
            <HistoryBars target={target} />
          </div>
        </CardContent>

        <CollapsibleContent>
          <CardContent className="pt-0 pb-6">
            <Separator className="mb-5" />
            <ReadinessBreakdown target={target} targetLabel={targetLabel} />
          </CardContent>
        </CollapsibleContent>

        <CollapsibleTrigger
          className="flex w-full items-center justify-center gap-1.5 border-t py-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent/50 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:outline-none"
          aria-label={
            open ? 'Hide readiness breakdown' : 'Show readiness breakdown'
          }
        >
          {open ? 'Hide breakdown' : 'How is this calculated?'}
          <ChevronDownIcon
            className={cn(
              'size-3.5 transition-transform duration-200',
              open && 'rotate-180',
            )}
            aria-hidden="true"
          />
        </CollapsibleTrigger>
      </Collapsible>
    </Card>
  )
}
